import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Truck, Package, Plus, Search, Filter, Download, Upload, ChevronDown, ChevronUp, MoreVertical, Eye, Edit, Trash2, AlertTriangle, CheckCircle, XCircle, Clock, FileText, Save, AlertCircle, Loader2 } from 'lucide-react'
import { cn, formatCurrency, formatNumber, formatDate } from '../../lib/utils'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { inventoryApi, queryKeys } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { Dropdown } from '../../components/ui/Dropdown'
import { Skeleton } from '../../components/ui/Skeleton'
import { Table } from '../../components/ui/Table'
import { Pagination } from '../../components/ui/Pagination'
import { useUIStore } from '../../stores/uiStore'
import { useAuthStore , usePermissions } from '../../stores/authStore'
import toast from 'react-hot-toast'

const statusConfig = {
  pending: { label: 'در انتظار', variant: 'warning' as const, icon: Clock },
  processing: { label: 'در حال پردازش', variant: 'info' as const, icon: Clock },
  completed: { label: 'تکمیل شده', variant: 'success' as const, icon: CheckCircle },
  cancelled: { label: 'لغو شده', variant: 'danger' as const, icon: XCircle },
}

export default function StockReceivePage() {
  const { hasPermission } = usePermissions()
  const { modals, openModal, closeModal } = useUIStore()
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<ReceiveListParams>({
    page: 1,
    page_size: 20,
    sort_by: 'created_at',
    sort_order: 'desc',
  })
  const [deletedId, setDeletedId] = useState<string | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.receives(filters),
    queryFn: () => inventoryApi.listReceives(filters),
    placeholderData: (prev) => prev,
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => inventoryApi.createReceive(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.receives() })
      toast.success('رسید دریافت ایجاد شد')
      closeModal('create-receive')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'ایجاد ناموفق بود'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => inventoryApi.deleteReceive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.receives() })
      toast.success('رسید دریافت حذف شد')
      closeModal('delete-confirm')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'حذف ناموفق بود'),
  })

  const handlePageChange = (page: number) => setFilters(prev => ({ ...prev, page }))
  const handlePageSizeChange = (size: number) => setFilters(prev => ({ ...prev, page_size: size, page: 1 }))
  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); setFilters(prev => ({ ...prev, page: 1 })) }

  const totalPages = data?.total_pages || 1
  const totalItems = data?.total || 0

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">دریافت کالا</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">مدیریت رسیدهای دریافت انبار</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => openModal('create-receive')} disabled={!hasPermission('stock', 'receive')}>
            <Upload className="w-4 h-4" />
            دریافت جدید
          </Button>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card p-4"
      >
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <Input placeholder="جستجو (شماره رسید، تامین‌کننده)..." className="flex-1 max-w-md" />
          <Select
            value={filters.status || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, status: v || undefined, page: 1 }))}
            options={[
              { value: '', label: 'همه وضعیت‌ها' },
              { value: 'pending', label: 'در انتظار' },
              { value: 'processing', label: 'در حال پردازش' },
              { value: 'completed', label: 'تکمیل شده' },
              { value: 'cancelled', label: 'لغو شده' },
            ]}
            placeholder="وضعیت"
            className="w-40"
          />
          <Select
            value={filters.warehouse_id || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, warehouse_id: v || undefined, page: 1 }))}
            options={[{ value: '', label: 'همه انبارها' }]}
            placeholder="انبار"
            className="w-40"
          />
          <Dropdown
            trigger={<Button variant="outline"><Filter className="w-4 h-4 ml-1" /> فیلترها</Button>}
            items={[
              { label: 'خروجی اکسل', icon: Download, onClick: () => {} },
              { type: 'divider' },
              { label: 'وارد کردن', icon: Upload, onClick: () => {} },
            ]}
          />
        </form>
      </motion.div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card overflow-hidden"
      >
        {isLoading ? (
          <div className="p-6"><SkeletonTable rows={5} cols={8} /></div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
            <p className="text-danger-600 dark:text-danger-400">خطا در بارگذاری</p>
            <Button variant="outline" onClick={() => refetch()} className="mt-4">تلاش مجدد</Button>
          </div>
        ) : data?.items.length === 0 ? (
          <div className="p-12 text-center">
            <Truck className="w-16 h-16 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">هیچ رسید دریافتی یافت نشد</h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-4">اولین رسید دریافت را ایجاد کنید</p>
            <Button onClick={() => openModal('create-receive')} disabled={!hasPermission('stock', 'receive')}>
              <Plus className="w-4 h-4 ml-1" />
              ایجاد رسید دریافت
            </Button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>شماره رسید</th>
                    <th className="hidden sm:table-cell">تاریخ</th>
                    <th className="hidden md:table-cell">تامین‌کننده</th>
                    <th className="hidden lg:table-cell">انبار مقصد</th>
                    <th>تعداد آیتم</th>
                    <th>مبلغ کل</th>
                    <th>وضعیت</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.items || []).map((receive, index) => (
                    <motion.tr
                      key={receive.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                    >
                      <td className="font-medium text-neutral-900 dark:text-white font-mono">{receive.receive_number}</td>
                      <td className="hidden sm:table-cell text-neutral-600 dark:text-neutral-400 text-sm">{formatDate(receive.receive_date)}</td>
                      <td className="hidden md:table-cell text-neutral-700 dark:text-neutral-300">{receive.supplier?.name || '—'}</td>
                      <td className="hidden lg:table-cell text-neutral-600 dark:text-neutral-400">{receive.warehouse?.name || '—'}</td>
                      <td className="font-mono text-sm text-neutral-700 dark:text-neutral-300">{receive.total_items}</td>
                      <td className="font-mono text-sm text-neutral-700 dark:text-neutral-300">{formatCurrency(receive.total_amount || 0)}</td>
                      <td>
                        <Badge variant={statusConfig[receive.status as keyof typeof statusConfig]?.variant || 'neutral'} className="text-xs">
                          {statusConfig[receive.status as keyof typeof statusConfig]?.label || receive.status}
                        </Badge>
                      </td>
                      <td>
                        <Dropdown
                          trigger={<button className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"><MoreVertical className="w-4 h-4" /></button>}
                          items={[
                            { label: 'مشاهده', icon: Eye, onClick: () => window.open(`/inventory/receive/${receive.id}`, '_blank') },
                            { label: 'ویرایش', icon: Edit, onClick: () => {}, disabled: !hasPermission('stock', 'receive') || receive.status !== 'pending' },
                            { type: 'divider' },
                            { label: 'حذف', icon: Trash2, className: 'text-danger-600', onClick: () => { setDeletedId(receive.id); openModal('delete-confirm') }, disabled: !hasPermission('stock', 'receive') || receive.status !== 'pending' },
                          ]}
                        />
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                نمایش {((filters.page - 1) * filters.page_size) + 1} تا {Math.min(filters.page * filters.page_size, totalItems)} از {totalItems.toLocaleString('fa-IR')}
              </div>
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                showPageSize
                pageSize={filters.page_size}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </>
        )}
      </motion.div>

      {/* Create Receive Modal */}
      <Modal
        isOpen={modals['create-receive']}
        onClose={() => closeModal('create-receive')}
        title="ایجاد رسید دریافت جدید"
        size="lg"
      >
        <ReceiveForm onSubmit={createMutation.mutate} onClose={() => closeModal('create-receive')} isLoading={createMutation.isPending} />
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={modals['delete-confirm']}
        onClose={() => { closeModal('delete-confirm'); setDeletedId(null) }}
        title="حذف رسید دریافت"
        size="sm"
      >
        <p className="text-neutral-600 dark:text-neutral-400">
          آیا از حذف این رسید دریافت مطمئن هستید؟ این عملیات قابل بازگشت نیست.
        </p>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => { closeModal('delete-confirm'); setDeletedId(null) }}>انصراف</Button>
          <Button variant="danger" onClick={() => deletedId && deleteMutation.mutate(deletedId)} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? <> <Loader2 className="w-4 h-4 animate-spin" /> در حال حذف... </> : 'حذف' }
          </Button>
        </div>
      </Modal>
    </div>
  )
}

const receiveItemSchema = z.object({
  product_id: z.string().uuid('محصول معتبر نیست'),
  variant_id: z.string().uuid().optional().nullable(),
  quantity: z.number().min(1, 'تعداد باید حداقل ۱ باشد'),
  unit_price: z.number().min(0, 'قیمت واحد باید مثبت باشد'),
  discount: z.number().min(0).default(0),
  tax_rate: z.number().min(0).max(100).default(9),
  warehouse_id: z.string().uuid('انبار معتبر نیست'),
  batch_number: z.string().optional(),
  expiry_date: z.string().optional().nullable(),
  serial_numbers: z.array(z.string()).optional(),
})

const receiveFormSchema = z.object({
  supplier_id: z.string().uuid('تامین‌کننده معتبر نیست'),
  warehouse_id: z.string().uuid('انبار مقصد معتبر نیست'),
  receive_date: z.string().min(1, 'تاریخ دریافت الزامی است'),
  due_date: z.string().optional().nullable(),
  reference_number: z.string().optional(),
  notes: z.string().optional(),
  items: z.array(receiveItemSchema).min(1, 'حداقل یک آیتم باید وجود داشته باشد'),
})

type ReceiveFormData = z.infer<typeof receiveFormSchema>

interface ReceiveFormProps {
  onSubmit: (data: ReceiveFormData) => void
  onClose: () => void
  isLoading: boolean
}

function ReceiveForm({ onSubmit, onClose, isLoading }: ReceiveFormProps) {
  const methods = useForm<ReceiveFormData>({
    resolver: zodResolver(receiveFormSchema),
    defaultValues: {
      supplier_id: '',
      warehouse_id: '',
      receive_date: new Date().toISOString().split('T')[0],
      due_date: '',
      reference_number: '',
      notes: '',
      items: [{
        product_id: '',
        variant_id: null,
        quantity: 1,
        unit_price: 0,
        discount: 0,
        tax_rate: 9,
        warehouse_id: '',
        batch_number: '',
        expiry_date: '',
        serial_numbers: [],
      }],
    },
  })

  const { control, watch, setValue } = methods
  const { fields: itemFields, append: appendItem, remove: removeItem } = useFieldArray({
    control,
    name: 'items',
  })

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller
          name="supplier_id"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select label="تامین‌کننده *" options={[{ value: '', label: 'انتخاب کنید' }]} placeholder="تامین‌کننده" {...field} />
          )}
        />
        <Controller
          name="warehouse_id"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select label="انبار مقصد *" options={[{ value: '', label: 'انتخاب کنید' }]} placeholder="انبار" {...field} />
          )}
        />
        <Controller
          name="receive_date"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input label="تاریخ دریافت *" type="date" {...field} />
          )}
        />
        <Controller
          name="due_date"
          control={control}
          render={({ field }) => (
            <Input label="تاریخ سررسید" type="date" {...field} />
          )}
        />
        <Controller
          name="reference_number"
          control={control}
          render={({ field }) => (
            <Input label="شماره مرجع / فاکتور خرید" placeholder="اختیاری" {...field} />
          )}
        />
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <Input label="یادداشت‌ها" placeholder="اختیاری" {...field} />
          )}
        />
      </div>

      <div className="border-t border-neutral-200 dark:border-neutral-800 pt-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-neutral-900 dark:text-white">آیتم‌های رسید</h4>
          <Button type="button" variant="outline" size="sm" onClick={() => appendItem({ product_id: '', variant_id: null, quantity: 1, unit_price: 0, discount: 0, tax_rate: 9, warehouse_id: '', batch_number: '', expiry_date: '', serial_numbers: [] })}>
            <Plus className="w-4 h-4 ml-1" />
            افزودن آیتم
          </Button>
        </div>

        {itemFields.map((field, index) => (
          <div key={field.id} className="card p-4 mb-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-medium text-neutral-900 dark:text-white">آیتم #{index + 1}</span>
              {itemFields.length > 1 && (
                <button type="button" onClick={() => removeItem(index)} className="p-1.5 rounded-lg text-neutral-400 hover:text-danger-600 dark:hover:text-danger-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Controller
                name={`items.${index}.product_id`}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select label="محصول *" options={[{ value: '', label: 'انتخاب کنید' }]} placeholder="محصول" {...field} />
                )}
              />
              <Controller
                name={`items.${index}.variant_id`}
                control={control}
                render={({ field }) => (
                  <Select label="تنوع" options={[{ value: '', label: 'پیش‌فرض' }]} placeholder="تنوع" {...field} />
                )}
              />
              <Controller
                name={`items.${index}.warehouse_id`}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select label="انبار *" options={[{ value: '', label: 'انتخاب کنید' }]} placeholder="انبار" {...field} />
                )}
              />
              <Controller
                name={`items.${index}.quantity`}
                control={control}
                rules={{ required: true, min: 1 }}
                render={({ field }) => (
                  <Input label="تعداد *" type="number" min={1} step={1} {...field} />
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Controller
                name={`items.${index}.unit_price`}
                control={control}
                rules={{ required: true, min: 0 }}
                render={({ field }) => (
                  <Input label="قیمت واحد (ریال) *" type="number" min={0} step={1000} {...field} />
                )}
              />
              <Controller
                name={`items.${index}.discount`}
                control={control}
                render={({ field }) => (
                  <Input label="تخفیف (ریال)" type="number" min={0} step={1000} {...field} />
                )}
              />
              <Controller
                name={`items.${index}.tax_rate`}
                control={control}
                render={({ field }) => (
                  <Input label="مالیات (%)" type="number" min={0} max={100} step={0.1} {...field} />
                )}
              />
              <Controller
                name={`items.${index}.batch_number`}
                control={control}
                render={({ field }) => (
                  <Input label="شماره بچ" placeholder="اختیاری" {...field} />
                )}
              />
            </div>

            <Controller
              name={`items.${index}.expiry_date`}
              control={control}
              render={({ field }) => (
                <Input label="تاریخ انقضا" type="date" {...field} />
              )}
            />
          </div>
        ))}

        {itemFields.length === 0 && (
          <div className="text-center py-8 border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-xl">
            <Package className="w-12 h-12 text-neutral-400 mx-auto mb-2" />
            <p className="text-neutral-500 dark:text-neutral-400">هیچ آیتمی اضافه نشده است</p>
            <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => appendItem({ product_id: '', variant_id: null, quantity: 1, unit_price: 0, discount: 0, tax_rate: 9, warehouse_id: '', batch_number: '', expiry_date: '', serial_numbers: [] })}>
              <Plus className="w-4 h-4 ml-1" />
              افزودن آیتم اول
            </Button>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-800">
        <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>انصراف</Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? <> <Loader2 className="w-4 h-4 animate-spin" /> در حال ثبت... </> : <> <Save className="w-4 h-4 ml-1" /> ثبت رسید دریافت </> }
        </Button>
      </div>
    </form>
  )
}

function SkeletonTable({ rows = 5, cols = 8 }: { rows?: number; cols?: number }) {
  return (
    <table className="table">
      <thead>
        <tr>{Array.from({ length: cols }).map((_, i) => <th key={i}><Skeleton variant="text" width="80%" /></th>)}</tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, i) => (
          <tr key={i}>{Array.from({ length: cols }).map((_, j) => <td key={j}><Skeleton variant="text" /></td>)}</tr>
        ))}
      </tbody>
    </table>
  )
}

type ReceiveListParams = {
  page: number
  page_size: number
  sort_by: string
  sort_order: 'asc' | 'desc'
  search?: string
  status?: string
  warehouse_id?: string
  supplier_id?: string
  date_from?: string
  date_to?: string
}